import { useState } from "react";
import { API } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Sparkle, PaperPlaneTilt, X } from "@phosphor-icons/react";

const STARTER_PROMPTS = [
  "Explain this business in plain English",
  "Are the profit margins healthy?",
  "What's the biggest risk investors watch?",
  "How does it make money?",
];

export default function AIExplainer({ open, onOpenChange, symbol, companyName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const ask = async (q) => {
    const question = q ?? input;
    if (!question.trim() || streaming) return;
    setInput("");
    const userMsg = { role: "user", content: question };
    const aiMsg = { role: "ai", content: "" };
    setMessages((m) => [...m, userMsg, aiMsg]);
    setStreaming(true);

    try {
      const r = await fetch(`${API}/ai/explain`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol, question }),
      });
      if (r.status === 402) {
        const err = await r.json();
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "ai", content: `${err.detail}\n\nTap "Upgrade" in the top bar to unlock unlimited AI.` };
          return copy;
        });
        return;
      }
      if (!r.ok || !r.body) throw new Error("stream failed");
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() || "";
        for (const c of chunks) {
          if (!c.startsWith("data:")) continue;
          const payload = c.slice(5).trimStart();
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "ai", content: `Error: ${payload}` };
              return copy;
            });
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "ai", content: (copy[copy.length - 1].content || "") + payload };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "ai", content: "Sorry — the AI is unreachable right now." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] bg-paper2 p-0 border-l border-black/20 rounded-none">
        <div className="relative h-full flex flex-col grain">
          <SheetHeader className="p-6 border-b border-black/10 bg-paper">
            <SheetTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkle weight="fill" size={20} className="text-coral" />
              Ask about {symbol}
            </SheetTitle>
            <p className="text-sm text-ink2">{companyName}</p>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="label-eyebrow">Try asking</p>
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => ask(p)}
                    className="w-full text-left p-3 bg-paper border border-black/10 hover:border-ikb hover:bg-ikb hover:text-paper transition-colors text-sm"
                    data-testid={`ai-prompt-${p.slice(0, 10)}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`animate-fade-up ${m.role === "user" ? "" : ""}`}>
                <div className="label-eyebrow mb-1">{m.role === "user" ? "You" : "StockProfit Life AI"}</div>
                <div className={`p-4 border ${m.role === "user" ? "bg-paper border-black/10" : "bg-ink text-paper border-ink"} whitespace-pre-wrap text-[15px] leading-relaxed`}>
                  {m.content || (streaming ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="p-4 border-t border-black/10 bg-paper flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask a question about ${symbol}…`}
              className="flex-1 px-4 py-3 bg-paper2 border border-black/15 focus:outline-none focus:border-ikb text-sm"
              data-testid="ai-input"
              disabled={streaming}
            />
            <button type="submit" className="btn-primary" disabled={streaming} data-testid="ai-submit">
              <PaperPlaneTilt weight="fill" size={16} />
            </button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
